// Used by Site Management page and Study Overview Locations tab
import React from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { AUTHORING_TASKS } from "constants/tasks.js"

// Components
import { PrimeButton } from "components-design-system"

import "./useSiteUploadActions.scss"
import "styles/global-animations.scss"

// context
import useAuthoringValidationActions from "containers/studies/Study/subcomponents/Authoring/hooks/actions/useAuthoringValidationActions"
import useAppState from "context/hooks/useAppState"

// helpers/utils
import usePrsUpload from "utilities/usePrsUpload/usePrsUpload"

dayjs.extend(relativeTime)

const useSiteUploadActions = ({
  prsUploadStudyList,
  onRegenerate,
  setTriggerExport,
  isUploadDisabled,
  canUploadToPRS,
  source,
  useOnGetPrsUpload,
  useOnPostPrsUpload,
  showExport,
  showValidate,
  lastImportDate,
  callbackAfterUpload,
  uploadToPrsMsg = "Send all protocol maintenance updates to PRS. If you would like to release data directly to CTG check the checkbox below.",
  studyData,
  type,
  disabledTooltip,
}) => {
  const { isUploading, UploadButton, UploadModals, CancelButton } = usePrsUpload({
    useOnGetPrsUpload,
    useOnPostPrsUpload,
    isUploadDisabled,
    prsUploadStudyList,
    uploadToPrsMsg,
    callbackAfterUpload,
    type,
    disabledTooltip,
  })

  const [isRegenerating, setIsRegenerating] = React.useState(false)
  const [regenClassNames, setRegenClassNames] = React.useState(["fa-light fa-arrow-down-to-line icons regen-icon"])
  const { onRegistryValidate } = useAuthoringValidationActions(studyData?.authoringId)
  const { taskType } = useAppState()

  React.useEffect(() => {
    if (taskType === source + "-regenerate") {
      setRegenClassNames(["fal fa-redo spin-active icons regen-icon"])
      setIsRegenerating(true)
    } else {
      setRegenClassNames(["fa-light fa-arrow-down-to-line icons regen-icon"])
      setIsRegenerating(false)
    }
  }, [taskType])

  const RegenerateButton = React.useMemo(() => {
    let date, tz, shortNameTZ, time
    if (lastImportDate) {
      date = new Date(lastImportDate).toLocaleString()
      tz = new Date(lastImportDate).toString().match(/\(([A-Za-z\s].*)\)/)[1]
      shortNameTZ = tz
        .split(" ")
        .map((word) => word[0])
        .join("")
      time = (
        <span>
          {dayjs(lastImportDate).fromNow()}:<br />
          {date.toLocaleString()} {shortNameTZ}{" "}
        </span>
      )
    }

    return (
      <PrimeButton
        icon={<i className={regenClassNames.join(" ")} />}
        tooltip={
          time ? (
            <>
              <div>Pull from PRS</div>
              <div>(Last pulled from PRS: {time})</div>
            </>
          ) : (
            "Pull from PRS"
          )
        }
        disabled={isRegenerating}
        onClick={onRegenerate}
        style={{ display: "flex", alignItems: "center" }}
      />
    )
  }, [taskType, isRegenerating, lastImportDate])

  const RegenerateIconButton = React.useMemo(() => {
    let date, tz, shortNameTZ, time
    if (lastImportDate) {
      date = new Date(lastImportDate).toLocaleString()
      tz = new Date(lastImportDate).toString().match(/\(([A-Za-z\s].*)\)/)[1]
      shortNameTZ = tz
        .split(" ")
        .map((word) => word[0])
        .join("")
      time = (
        <span>
          {dayjs(lastImportDate).fromNow()}:<br />
          {date.toLocaleString()} {shortNameTZ}{" "}
        </span>
      )
    }

    return (
      <PrimeButton
        icon={<i className={regenClassNames.join(" ")} />}
        tooltip={
          time ? (
            <>
              <div>Pull from PRS</div>
              <div>(Last pull from PRS: {time})</div>
            </>
          ) : (
            "Pull from PRS"
          )
        }
        loading={isRegenerating}
        onClick={onRegenerate}
        // type="primary"
        style={{ display: "flex", alignItems: "center" }}
      ></PrimeButton>
    )
  }, [taskType, isRegenerating, lastImportDate])

  const ValidateIconButton = (
    <PrimeButton
      icon={<i className="fal fa-file-check" />}
      tooltip="Validate"
      loading={AUTHORING_TASKS.prsPreview === taskType}
      onClick={() => onRegistryValidate({ form_view: "ctg" })}
    />
  )

  const ValidateButton = (
    <PrimeButton
      tooltip="Validate"
      type="primary"
      icon={<i className="fal fa-file-check" />}
      loading={AUTHORING_TASKS.prsPreview === taskType}
      onClick={() => onRegistryValidate({ form_view: "ctg" })}
    >
      Validate
    </PrimeButton>
  )

  const ExportButton = (
    <PrimeButton
      icon={<i className="fal fa-file-export" />}
      onClick={() => setTriggerExport(true)}
      tooltip={"Export"}
    />
  )

  const HeaderActionComponents = (
    <div className="header-actions">
      <div className="inner-header-actions">
        {showValidate && ValidateIconButton}
        {RegenerateButton}
        {showExport && ExportButton}
        {canUploadToPRS && UploadButton}
        {UploadModals}
      </div>
      {isUploading && CancelButton}
    </div>
  )

  return {
    HeaderActionComponents,
    RegenerateButton,
    RegenerateIconButton,
    UploadButton,
    CancelButton,
    ValidateIconButton,
    ExportButton,
    isUploading,
    ValidateButton,
    UploadModals,
  }
}

export default useSiteUploadActions
